import { CbhIcon, Image, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";

export interface ShareScoreCardProps {
  id?: string;
  profileImageUrl: string | undefined;
  fullName: string;
  qualification: string | undefined;
  cityState: string;
  clipboardScore: number;
  rank: number;
  outOf: number;
  shiftsWorked: number;
  totalEarningsInCents: number;
}

export function ShareScoreCard(props: ShareScoreCardProps) {
  const {
    id,
    profileImageUrl,
    fullName,
    qualification,
    cityState,
    clipboardScore,
    rank,
    outOf,
    shiftsWorked,
    totalEarningsInCents,
  } = props;

  return (
    <Box
      id={id}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        background:
          "linear-gradient(180deg, rgba(218, 241, 232, 1) 0%, rgba(251, 242, 232, 1) 100%)",
        padding: 6,
        width: "100%",
      }}
    >
      {/* Badge card */}
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: "20px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.12)",
          paddingTop: 3,
          paddingBottom: 5,
          paddingX: 5,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Badge notch — colored to match gradient background, giving a see-through cutout effect */}
        <Box
          sx={{
            width: 72,
            height: 10,
            backgroundColor: "rgba(218, 241, 232, 1)",
            borderRadius: "5px",
            marginBottom: 3,
          }}
        />
        {/* Profile Picture */}
        <Box sx={{ marginBottom: 3 }}>
          {isDefined(profileImageUrl) ? (
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "12px",
                overflow: "hidden",
              }}
            >
              <Image
                src={profileImageUrl}
                alt="Profile"
                width="100%"
                height="100%"
                sx={{ objectFit: "cover" }}
              />
            </Box>
          ) : (
            <CbhIcon type="clipboard-bunny-colored" size="xLarge" />
          )}
        </Box>

        {/* Name, Qualification, and City/State */}
        <Stack spacing={0.5} alignItems="center" sx={{ marginBottom: 3 }}>
          <Text semibold variant="h6" textAlign="center">
            {fullName}
            {isDefined(qualification) && qualification.length > 0 && (
              <>
                {": "}
                <Text inline variant="h6" color={(theme) => theme.palette.text.secondary}>
                  {qualification}
                </Text>
              </>
            )}
          </Text>
          {isDefined(cityState) && cityState.length > 0 && (
            <Text variant="caption" color={(theme) => theme.palette.text.secondary}>
              {cityState}
            </Text>
          )}
        </Stack>

        {/* Score */}
        <Stack spacing={0.5} alignItems="center" sx={{ marginBottom: 3 }}>
          <Text
            sx={{
              fontSize: "72px",
              lineHeight: 1,
              color: (theme) => theme.palette.success.dark,
            }}
          >
            {clipboardScore}
          </Text>
          <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
            Clipboard Score
          </Text>
        </Stack>

        {/* Stats Card */}
        <Box
          sx={{
            width: "100%",
            border: "1px solid",
            borderColor: (theme) => theme.palette.divider,
            borderRadius: "16px",
            padding: 3,
          }}
        >
          {/* Earnings */}
          <Stack alignItems="center" paddingBottom={2}>
            <Text
              semibold
              sx={{
                fontSize: "20px",
                color: (theme) => theme.palette.success.dark,
              }}
            >
              {formatCentsAsUsd(totalEarningsInCents, { maximumFractionDigits: 0 })}
            </Text>
            <Stack direction="row" spacing={1} alignItems="center">
              <CbhIcon type="bank" size="xSmall" />
              <Text variant="caption" color={(theme) => theme.palette.text.secondary}>
                Total Earnings
              </Text>
            </Stack>
          </Stack>

          <Box sx={{ borderTop: "1px solid", borderColor: (theme) => theme.palette.divider }} />

          {/* Shifts Worked and Rank */}
          <Stack direction="row" paddingTop={2} justifyContent="space-around">
            <Stack alignItems="center" flex={1}>
              <Text semibold variant="h6">
                {shiftsWorked.toLocaleString()}
              </Text>
              <Stack direction="row" spacing={1} alignItems="center">
                <CbhIcon type="calendar-with-cells" size="xSmall" />
                <Text variant="caption" color={(theme) => theme.palette.text.secondary}>
                  Shifts Worked
                </Text>
              </Stack>
            </Stack>

            <Box
              sx={{
                alignSelf: "stretch",
                borderLeft: "1px solid",
                borderColor: (theme) => theme.palette.divider,
              }}
            />

            <Stack alignItems="center" flex={1}>
              <Text semibold variant="h6">
                {rank}
                <Text inline variant="body2" color={(theme) => theme.palette.text.secondary}>
                  /{outOf}
                </Text>
              </Text>
              <Stack direction="row" spacing={1} alignItems="center">
                <CbhIcon type="map-pin" size="xSmall" />
                <Text variant="caption" color={(theme) => theme.palette.text.secondary}>
                  Local Rank
                </Text>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
